import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { SettingsFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { FormikProps } from "formik";
import { EditSalaryFormContainerProps } from "models/propTypes/settingsPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import * as Yup from "yup";
//#endregion
//#region components
import EditSalariesForm from "./editSalariesForm";
import useHighSalaryWarningDialog from "./useHighSalaryWarningDailog";
import { useSalaryService } from "serviceHooks";
//#endregion
import { DateHelper, FormHelper, LocalizationHelperInstance } from "utils/helpers";

const EditSalariesFormContainer: ForwardRefRenderFunction<FormHandles, EditSalaryFormContainerProps> = (props, ref) => {
  const salaryService = useSalaryService();
  const { settings } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const getHighSalaryWarningDialog = useHighSalaryWarningDialog();

  const initialValues = {
    salarySettings:
      settings?.map((e) => ({
        id: e.id,
        name: e.name,
        bankAccount: e.salaryInfo?.bankAccount,
        bankAccountClearing: e.salaryInfo?.bankAccountClearing,
        monthlySalary: e.salaryInfo?.monthlySalary,
        employmentRate: e.salaryInfo?.employmentRate,
        taxTable: e.salaryInfo?.taxTable,
        taxTableName:
          e.salaryInfo?.taxTable === WintSuperkollAPIModel.TaxTableInfo.None
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.None)
            : e.salaryInfo?.taxTable.toString(),
        employmentStartDate: e.salaryInfo?.employmentStartDate,
        employmentEndDate: e.salaryInfo?.employmentEndDate,
        salaryActive: e.salaryInfo?.isActive,
        salaryChangeFrom: e.salaryInfo?.salaryChangeFrom,
        workplaceStreetAddress: e.salaryInfo?.workPlaceStreetAddress,
        workplaceCity: e.salaryInfo?.workPlaceCity,
        position: e.salaryInfo?.position,
        vacationConditions: e.salaryInfo?.vacationConditions,
        workType: e.salaryInfo?.workType,
        usualWorkHours: e.salaryInfo?.usualWorkHours,
        schedule: e.salaryInfo?.schedule,
      })) ?? [],
  };

  const validationSchema = Yup.object<SettingsFormTypes.SalarySettings>({
    salarySettings: Yup.array().of(
      Yup.object<SettingsFormTypes.SalarySetting>({
        id: Yup.number(),
        name: Yup.string(),
        bankAccount: Yup.string(),
        bankAccountClearing: Yup.string(),
        monthlySalary: Yup.number().when(
          FormFieldConstants.Settings.SalarySetting.SalaryActivated,
          ([salaryActivated], schema: Yup.NumberSchema) =>
            salaryActivated ? schema.required(TranslationKeys.Validations.Required) : schema
        ),
        employmentRate: Yup.number()
          .min(0, TranslationKeys.Settings.EmploymentRateValidation)
          .max(100, TranslationKeys.Settings.EmploymentRateValidation)
          .required(TranslationKeys.Validations.Required),
        taxTable: Yup.number(),
        taxTableName: Yup.string().when(
          FormFieldConstants.Settings.SalarySetting.SalaryActive,
          ([salaryActive], schema: Yup.StringSchema) =>
            salaryActive ? schema.required(TranslationKeys.Validations.Required) : schema
        ),
        employmentStartDate: Yup.date(),
        employmentEndDate: Yup.date(),
        salaryActive: Yup.boolean(),
        salaryChangeFrom: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .when(
            [
              FormFieldConstants.Settings.SalarySetting.SalaryChanged,
              FormFieldConstants.Settings.SalarySetting.SalaryActive,
            ],
            ([salaryChanged, isSalaryActive], schema: Yup.DateSchema) =>
              salaryChanged && isSalaryActive ? schema.required(TranslationKeys.Validations.Required) : schema
          )
          .when(
            FormFieldConstants.Settings.SalarySetting.EmploymentStartDate,
            ([employmentStartDate], schema: Yup.DateSchema) =>
              employmentStartDate && DateHelper.isValidDate(employmentStartDate)
                ? schema.min(
                    employmentStartDate,
                    TranslationKeys.Validations.ChangeSalaryDateMustBeLaterThanEmploymentDate
                  )
                : schema
          ),
        salaryChanged: Yup.boolean(),
        workplaceStreetAddress: Yup.string().max(50),
        workplaceCity: Yup.string().max(50),
      })
    ),
  });

  const handleSubmit = (values: SettingsFormTypes.SalarySettings) => {
    const settingsPost: WintSuperkollAPIModel.EmployeesSalarySettings = {
      salarySettings: values.salarySettings.map((v) => {
        const originalData = settings.find((d) => d.id === v.id);
        return {
          employeeId: v.id,
          bankAccount: originalData?.salaryInfo?.bankAccount,
          bankAccountClearing: originalData?.salaryInfo?.bankAccountClearing,
          monthlySalary: v.monthlySalary,
          taxTable: v.taxTable!,
          employmentRate: v.employmentRate!,
          employmentStartDate: v.employmentStartDate,
          employmentEndDate: v.employmentEndDate,
          isActive: v.salaryActive ?? false,
          salaryChangeFrom:
            v.monthlySalary !== originalData?.salaryInfo?.monthlySalary ? v.salaryChangeFrom : undefined,
          employmentRateChangeFrom:
            v.employmentRate !== originalData?.salaryInfo?.employmentRate ? v.salaryChangeFrom : undefined,
          workPlaceStreetAddress: v.workplaceStreetAddress,
          workPlaceCity: v.workplaceCity,
          position: v?.position,
          vacationConditions: v?.vacationConditions,
          workType: v?.workType,
          usualWorkHours: v?.usualWorkHours,
          schedule: v?.schedule,
        };
      }),
    };
    return salaryService.updateEmployeesSalarySettings(settingsPost);
  };

  const onSalaryChange = (name: string, value: any, formikProps: FormikProps<SettingsFormTypes.SalarySettings>) => {
    const { initialValues, setFieldValue } = formikProps;

    const fieldInfo = FormHelper.getTableRowFieldInfo(name);
    if (fieldInfo) {
      const { rowIndex, innerFieldName } = fieldInfo;
      let initialSalaryValue;

      if (innerFieldName === FormFieldConstants.Settings.SalarySetting.MonthlySalary) {
        initialSalaryValue = initialValues.salarySettings[rowIndex].monthlySalary;
      } else if (innerFieldName === FormFieldConstants.Settings.SalarySetting.EmploymentRate) {
        initialSalaryValue = initialValues.salarySettings[rowIndex].employmentRate;
      }

      setFieldValue(
        `${FormFieldConstants.Settings.SalarySettings.SalarySettings}[${+rowIndex}].${
          FormFieldConstants.Settings.SalarySetting.SalaryChanged
        }`,
        (initialSalaryValue ?? "") !== (value ?? "")
      );

      if (innerFieldName === FormFieldConstants.Settings.SalarySetting.MonthlySalary && value > 100000) {
        dispatch(displayDialog(getHighSalaryWarningDialog()));
      }
    }
  };

  const onSalaryToggle = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<SettingsFormTypes.SalarySettings>
  ) => {
    const { initialValues, values, setFieldValue } = formikProps;

    const fieldInfo = FormHelper.getTableRowFieldInfo(name);
    if (fieldInfo) {
      const { rowIndex } = fieldInfo;

      const initialRowValues = initialValues.salarySettings[rowIndex];
      const currentRowValues = values.salarySettings[rowIndex];
      const salaryActive = currentRowValues.salaryActive;

      if (salaryActive) {
        if (!initialRowValues?.salaryActive && !currentRowValues.salaryActivated) {
          setFieldValue(
            `${FormFieldConstants.Settings.SalarySettings.SalarySettings}[${+rowIndex}].${
              FormFieldConstants.Settings.SalarySetting.SalaryActivated
            }`,
            true
          );
        }
      }
    }
  };

  return (
    <EditSalariesForm
      ref={ref}
      {...props}
      defaultValues={initialValues}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      salaryChangeHandler={onSalaryChange}
      salaryToggleHandler={onSalaryToggle}
    />
  );
};

export default forwardRef(EditSalariesFormContainer);
